import type { TestEvent } from '../types/index.js';
import type { Logger } from '../utils/logger.js';
import { createNoopLogger } from '../utils/logger.js';
import { sizeOf } from '../utils/sizeOf.js';
import { PartialBatchError } from '../reporter/errors.js';

export interface EventBufferOptions {
  maxSize?: number;
  maxBytes?: number;
  flushIntervalMs?: number;
  onFlush?: (events: TestEvent[]) => Promise<void>;
  logger?: Logger;
}

export class EventBuffer {
  private events: TestEvent[] = [];
  private pendingBytes = 0;
  private maxSize: number;
  private maxBytes: number;
  private flushIntervalMs: number;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private onFlush: (events: TestEvent[]) => Promise<void>;
  private flushPromise: Promise<void> | null = null;
  private readonly logger: Logger;

  constructor(options: EventBufferOptions) {
    this.maxSize = options.maxSize ?? 10;
    this.maxBytes = options.maxBytes ?? Number.POSITIVE_INFINITY;
    this.flushIntervalMs = options.flushIntervalMs ?? 250;
    this.onFlush = options.onFlush ?? (async () => {});
    this.logger = options.logger ?? createNoopLogger();
    this.startTimer();
  }

  // Sync path for test:end — void flush so a stuck Kafka send cannot block enqueue.
  enqueue(event: TestEvent): void {
    this.events.push(event);
    this.pendingBytes += sizeOf(event);
    if (this.events.length >= this.maxSize || this.pendingBytes >= this.maxBytes) {
      void this.flush();
    }
  }

  async add(event: TestEvent): Promise<void> {
    this.events.push(event);
    this.pendingBytes += sizeOf(event);
    if (this.events.length >= this.maxSize || this.pendingBytes >= this.maxBytes) {
      await this.flush();
    }
  }

  // Resolves only when nothing is in flight AND the buffer is empty: a waiter
  // that returned on "empty" alone let onEnd send run:end while a flush started
  // by another waiter was still in flight (the server then finalized early).
  async flush(): Promise<void> {
    while (this.flushPromise) {
      try {
        await this.flushPromise;
      } catch {
        /* fall through: restored events retry on the next flush */
      }
    }

    if (this.events.length === 0) return;

    this.flushPromise = this.doFlush();
    try {
      await this.flushPromise;
    } finally {
      this.flushPromise = null;
    }
    if (this.events.length > 0) return this.flush();
  }

  private async doFlush(): Promise<void> {
    const eventsToFlush = [...this.events];
    const bytesToFlush = this.pendingBytes;
    this.events = [];
    this.pendingBytes = 0;
    try {
      await this.onFlush(eventsToFlush);
    } catch (error) {
      // Restore only the failed suffix; peers already acked in this flush.
      if (error instanceof PartialBatchError) {
        this.events = [...error.failed, ...this.events];
        this.pendingBytes += error.failed.reduce((sum, e) => sum + sizeOf(e), 0);
        this.logger.error(
          `Partial flush failure: ${error.succeeded} events delivered, ${error.failed.length} restored — ${error.message}`
        );
        throw error;
      }
      // Restore to FRONT — preserves order; next flush retries.
      this.events = [...eventsToFlush, ...this.events];
      this.pendingBytes += bytesToFlush;
      this.logger.error(`Failed to flush events: ${error instanceof Error ? error.message : String(error)}`);
      throw error;
    }
  }

  size(): number {
    return this.events.length;
  }

  clear(): void {
    this.events = [];
    this.pendingBytes = 0;
  }

  private startTimer(): void {
    if (this.flushIntervalMs > 0) {
      this.flushTimer = setInterval(() => {
        // void: doFlush restores events on failure, next cycle retries.
        void this.flush();
      }, this.flushIntervalMs);
    }
  }

  // Does NOT flush — caller does a final explicit flush before destroy.
  destroy(): void {
    if (this.flushTimer !== null) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }

  getEvents(): TestEvent[] {
    return [...this.events];
  }

  // Sync take-and-reset (no await between) so an enqueue racing the returned batch's send is neither double-sent nor dropped.
  drain(): TestEvent[] {
    const events = this.events;
    this.events = [];
    this.pendingBytes = 0;
    return events;
  }
}
